import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Checkbox } from "@mui/material";
import "./PurchaseFilter.css";
/*istanbul ignore next */
const PurchaseFilter = ({
  open,
  onClose,
  filterOptions,
  onApply,
  selectedFilters,
  onFilterChange,
}) => {
  const [activeCategory, setActiveCategory] = useState("contentOriginators");
  const [localFilters, setLocalFilters] = useState(selectedFilters || {});

  useEffect(() => {
    if (open) {
      setLocalFilters(selectedFilters || {});
      if (filterOptions.length > 0) {
        setActiveCategory(filterOptions[0].field);
      }
    }
  }, [open, selectedFilters, filterOptions]);

  const handleCategoryClick = (field) => {
    setActiveCategory(field);
  };

  const handleCategoryKeyDown = (e, field) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCategoryClick(field);
    }
  };

  const handleCheckboxChange = (field, value) => {
    const currentValues = localFilters[field] || [];
    let newValues;

    if (currentValues.includes(value)) {
      newValues = currentValues.filter((v) => v !== value);
    } else {
      newValues = [...currentValues, value];
    }

    const updatedFilters = {
      ...localFilters,
      [field]: newValues,
    };

    // Clear states if all countries are deselected
    if (field === "countries" && newValues.length === 0) {
      updatedFilters.states = [];
    }

    setLocalFilters(updatedFilters);

    // Notify parent component about filter changes for countries
    if (field === "countries" && onFilterChange) {
      onFilterChange(updatedFilters);
    }
  };

  const handleReset = () => {
    const emptyFilters = {};
    setLocalFilters(emptyFilters);
    if (onFilterChange) {
      onFilterChange(emptyFilters);
    }
    onApply(emptyFilters);
    onClose();
  };

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const getActiveCategoryOptions = () => {
    const category = filterOptions.find((opt) => opt.field === activeCategory);
    return category?.options || [];
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="purchase-filter-modal"
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
      }}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "80px 20px 20px 20px",
      }}
    >
      <div className="purchase-filter-container">
        <div className="purchase-filter-content">
          <div className="filter-sidebar">
            {filterOptions.map((option) => (
              <button
                key={option.field}
                className={`filter-category ${
                  activeCategory === option.field ? "active" : ""
                }`}
                onClick={() => handleCategoryClick(option.field)}
                onKeyDown={(e) => handleCategoryKeyDown(e, option.field)}
                type="button"
                aria-pressed={activeCategory === option.field}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/* Right Panel - Checkboxes */}
          <div className="filter-options-panel">
            <div className="filter-options-scroll">
              {getActiveCategoryOptions().map((opt) => (
                <label key={opt.value} className="filter-checkbox-item">
                  <Checkbox
                    checked={(localFilters[activeCategory] || []).includes(
                      opt.value,
                    )}
                    onChange={() =>
                      handleCheckboxChange(activeCategory, opt.value)
                    }
                    size="small"
                    sx={{
                      color: "#666",
                      "&.Mui-checked": {
                        color: "#1dcaff",
                      },
                    }}
                  />
                  <span className="filter-option-label">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="filter-footer">
          <Button
            variant="text"
            onClick={handleReset}
            className="filter-reset-btn"
          >
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            className="filter-apply-btn"
          >
            Apply
          </Button>
        </div>
      </div>
    </Modal>
  );
};

PurchaseFilter.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  filterOptions: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.any.isRequired,
          label: PropTypes.string.isRequired,
        }),
      ).isRequired,
    }),
  ).isRequired,
  onApply: PropTypes.func.isRequired,
  selectedFilters: PropTypes.object,
  onFilterChange: PropTypes.func,
};

PurchaseFilter.defaultProps = {
  selectedFilters: {},
  onFilterChange: undefined,
};

export default PurchaseFilter;
